import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { ErrorState, LoadingState } from '@/components/composites/states'
import { usePermission } from '@/features/permissions/usePermission'
import { crm2Api, useCrm2Settings, useCrm2Write } from './api'
import { ImportPanel } from './ImportPanel'
import { Crm2Frame, Field, PersonSelect, Section } from './shared'

/** The switch for production, who gets the tech tasks, and the sample companies for the staging preview. */
export function SettingsPage() {
  const { can } = usePermission()
  const settings = useCrm2Settings()
  const configure = can('manage', 'Crm')
  const save = useCrm2Write(crm2Api.saveSettings, () => 'Saved')
  const seed = useCrm2Write(crm2Api.seed, (r) => (r.created ? `${r.created} sample companies added` : 'The samples are already there'))
  const clear = useCrm2Write(crm2Api.clear, (r) => `${r.removed} sample companies removed`)
  return (
    <Crm2Frame title="Settings" description={settings.data?.production ? "The switch for TapCRMv2 and TapPM, and who gets the tech tasks." : "TapCRMv2 and TapPM are on in staging. Production stays off until it is switched on there, deliberately."}>
      {settings.error ? (
        <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />
      ) : settings.isPending ? (
        <LoadingState label="Loading settings" />
      ) : (
        <div className="grid max-w-4xl gap-4">
          <Section title="Switched on">
            <div className="flex items-center gap-3 px-4 py-3 text-sm">
              <Switch checked={settings.data.on} disabled={!configure || !settings.data.production} onCheckedChange={(v) => save.mutate({ enabled: v })} aria-label="Switched on" />
              <span>{settings.data.production ? (settings.data.on ? 'On in production' : 'Off in production') : 'Always on here (staging). The switch only matters in production.'}</span>
            </div>
          </Section>
          <Section title="Tech lead">
            <div className="grid gap-2 px-4 py-3">
              <Field label="Gets the pilot set-up tasks when a project opens at Pre-Pilot">
                <PersonSelect label="Tech lead" value={settings.data.techLeadId} onChange={(v) => configure && save.mutate({ techLeadId: v })} />
              </Field>
            </div>
          </Section>
          {configure ? <ImportPanel canRun={configure} /> : null}
          {!settings.data.production ? (
            <Section title="Sample companies">
              <div className="grid gap-3 px-4 py-3 text-sm">
                <p className="text-muted-foreground">Eleven made-up companies spread over every pipeline (one in each Marketing stage, three in Sales, a won client in Post-sales, a lost one, a partner and a podcast guest), so the layout can be judged with something in it.</p>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => seed.mutate(undefined)} disabled={!configure || seed.isPending}>Add sample companies</Button>
                  <Button size="sm" variant="outline" onClick={() => clear.mutate(undefined)} disabled={!configure || clear.isPending}>Remove them</Button>
                </div>
              </div>
            </Section>
          ) : null}
        </div>
      )}
    </Crm2Frame>
  )
}
